import React, {
	useState,
	useRef,
	useEffect,
} from 'react';
import style from './ContentManagerDropdown.module.css';
import CustomIcon from '../../Common/CustomIcon/CustomIcon';

/**
 * Custom dropdown component for selecting content managers with checkbox behavior.
 * Selected items remain in the list with checkboxes and are greyed out.
 */
const ContentManagerDropdown = ({
	options,
	initialValue,
	onChange,
	label,
}) => {
	const [ isOpen, setIsOpen, ] = useState(false);
	const [ selectedValues, setSelectedValues, ] = useState(() => {
		// Initialize selected values from initialValue prop
		return new Set((initialValue || [])
			.filter((item) => {
				return item && item.value;
			})
			.map((item) => {
				return item.value;
			}));
	});
	const dropdownRef = useRef(null);

	// Sync selectedValues with initialValue when it changes
	useEffect(() => {
		const newSelectedValues = new Set((initialValue || [])
			.filter((item) => {
				return item && item.value;
			})
			.map((item) => {
				return item.value;
			}));
		setSelectedValues(newSelectedValues);
	}, [ initialValue, ]);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (e) => {
			if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [ isOpen, ]);

	const handleToggle = (optionValue) => {
		const newSelectedValues = new Set(selectedValues);
		
		if (newSelectedValues.has(optionValue)) {
			newSelectedValues.delete(optionValue);
		} else {
			newSelectedValues.add(optionValue);
		}

		setSelectedValues(newSelectedValues);

		// Call onChange with the selected options in the same format as react-select
		const selectedOptions = options.filter((opt) => {
			return newSelectedValues.has(opt.value);
		});
		if (onChange) {
			onChange(selectedOptions);
		}
	};

	const selectedCount = selectedValues.size;
	const displayText = selectedCount === 0 ?
		'Select content managers...' :
		`${selectedCount} selected`;

	return (
		<div className={style.dropdownContainer} ref={dropdownRef}>
			{label && <label className={style.dropdownLabel}>{label}</label>}

			<div
				className={style.dropdownHeader}
				onClick={() => {
					return setIsOpen(!isOpen);
				}}
			>
				<span className={style.dropdownHeaderText}>{displayText}</span>
				<CustomIcon
					icon={isOpen ? 'caret-up' : 'caret-down'}
					className={style.dropdownCaret}
				/>
			</div>

			{isOpen && (
				<div className={style.dropdownList}>
					{options.length === 0 ? (
						<div className={style.dropdownEmpty}>No content managers available</div>
					) : (
						options.map((option) => {
							const isSelected = selectedValues.has(option.value);
							
							return (
								<div
									key={option.value}
									className={`${style.dropdownItem} ${isSelected ? style.dropdownItemSelected : ''}`}
									onClick={(e) => {
										e.stopPropagation();
										return handleToggle(option.value);
									}}
								>
									<div className={style.checkbox}>
										{isSelected ? (
											<CustomIcon icon="check-square" className={style.checkboxChecked} />
										) : (
											<CustomIcon icon={[ 'far', 'square', ]} className={style.checkboxUnchecked} />
										)}
									</div>
									<span className={style.dropdownItemLabel}>
										{option.label}
									</span>
								</div>
							);
						})
					)}
				</div>
			)}
		</div>
	);
};

export default ContentManagerDropdown;
